import React, { useEffect, useState } from "react";
import { ArrowRight, ChartNoAxesColumn, CirclePlay, Code, Eye, EyeOff, FileText, Folder, Headphones, Sparkles, User, UserPlus, Users } from "lucide-react";
import "./LoginScreen.css";
import agenticHrLogo from "./assets/agentichr-logo.png";
import agenticHrLogoBg from "./assets/bg-logo.png";
import agenticHrLogoTrans from "./assets/logo-transparent.png";

function GoogleG({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.1C12.4 13.6 17.7 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v8.5h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17z" />
      <path fill="#FBBC05" d="M10.5 28.7c-.5-1.5-.8-3.1-.8-4.7s.3-3.2.8-4.7l-7.9-6.1C.9 16.4 0 20.1 0 24s.9 7.6 2.6 10.8l7.900-6.1z" />
      <path fill="#34A853" d="M24 48c6.500 0 11.9-2.1 15.900-5.8l-7.500-5.8c-2.100 1.400-4.800 2.3-8.400 2.300-6.300 0-11.600-4.1-13.500-9.800l-7.900 6.1C6.500 42.6 14.600 48 24 48z" />
    </svg>
  );
}

/* Static, decorative product preview (tilted dashboard) shown in the hero. */
const BAR_W = { Frontend: 90, Backend: 102, QA: 56, DevOps: 40, Product: 30 };
function DashboardPreview() {
  const skills = [
    ["Frontend", 42, "#7b8cff", "#8f7bff"],
    ["Backend", 36, "#6f9bff", "#7fb0ff"],
    ["QA", 28, "#6cc6ff", "#7cd0ff"],
    ["DevOps", 18, "#b7a6ff", "#c9b8ff"],
    ["Product", 12, "#ffb3d1", "#ffc4dc"],
  ];
  const nav = [["Overview", 1], ["People"], ["Projects"], ["Hiring"], ["Interviews"], ["Offers & Joining"], ["Reports"]];
  return (
    <div className="lw-mock" aria-hidden="true">
      <div className="lw-note">
        <span>From workforce planning<br />to hiring, in one place</span>
        <svg width="34" height="40" viewBox="0 0 34 40" fill="none"><path d="M26 4 C 30 14, 22 24, 10 30" stroke="#dfe3f5" strokeWidth="1.6" strokeLinecap="round" /><path d="M8 24 L10 31 L17 32" stroke="#dfe3f5" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </div>
      <div className="lw-mock-plane">
        <div className="lw-mock-side">
          <div className="lw-mock-brand"><img src={agenticHrLogo} alt="Lyra Workforce" /><b>Lyra Workforce</b></div>
          {nav.map(([label, active]) => (
            <div key={label} className={"lw-mock-nav" + (active ? " active" : "")}><i />{label}</div>
          ))}
        </div>
        <div className="lw-mock-main">
          <div className="lw-mock-title">Workforce Overview</div>
          <div className="lw-mock-sub">Real-time view of your people, projects and hiring pipeline.</div>
          <div className="lw-mock-stats">
            <div><b>342</b><span>Employees</span><Users size={15} color="#3f7cff" /></div>
            <div><b>18</b><span>Active Projects</span><Folder size={15} color="#19b8c9" /></div>
            <div><b>6</b><span>Open Roles</span><span className="lw-mock-person"><User size={13} color="#7a5cf0" /></span></div>
          </div>
          <div className="lw-mock-skill">
            <div className="lw-mock-card-title">Workforce by Skill <em>➜</em></div>
            {skills.map(([label, value, a, b]) => (
              <div key={label} className="lw-mock-row"><span>{label}</span><i style={{ width: BAR_W[label], background: `linear-gradient(90deg,${a},${b})` }} /><b>{value}</b></div>
            ))}
          </div>
        </div>
        <div className="lw-mock-pipe">
          <div className="lw-mock-card-title">Hiring Pipeline <em>⌄</em></div>
          <div className="lw-donut"><div><b>24</b><span>Candidates</span></div></div>
          {[["Screening", 10, "#4f7cff"], ["Interviews", 8, "#ff9a5a"], ["Offer", 4, "#ffb44f"], ["Hired", 2, "#31c3a2"]].map(([label, value, color]) => (
            <div key={label} className="lw-mock-legend"><i style={{ background: color }} /><span>{label}</span><b>{value}</b></div>
          ))}
        </div>
      </div>
    </div>
  );
}

const FEATURES = [
  [<Users size={26} strokeWidth={2.2} />, "lw-ic-purple", <>Understand<br />Your Workforce</>, "Skills, availability, utilization and capacity."],
  [<Folder size={26} strokeWidth={2.2} fill="currentColor" />, "lw-ic-blue", "Plan & Allocate", "Match people to projects with AI suggestions."],
  [<ChartNoAxesColumn size={26} strokeWidth={2.6} />, "lw-ic-green", "Identify Gaps", "See what's missing before you hire."],
  [<UserPlus size={26} strokeWidth={2.2} />, "lw-ic-orange", "Hire with Confidence", "Source, screen, interview and onboard — all in one flow."],
];
const STATS = [
  ["30%", "lw-st-purple", "faster hiring cycles"],
  ["40%", "lw-st-blue", "better resource utilization"],
  ["End-to-end", "lw-st-green", "hiring workflow"],
  ["Full visibility", "lw-st-indigo", "from workforce to joining"],
];
const PLATFORM = [
  [<Code size={20} />, "Engineering"],
  [<Headphones size={20} />, "CX"],
  [<ChartNoAxesColumn size={20} />, "Finance"],
  [<FileText size={20} />, "Legal"],
  [<Sparkles size={20} />, "and more..."],
];

export function LoginHero({ onGetStarted }) {
  return (
    <div className="lw-left">
      <svg className="lw-wave" viewBox="0 0 966 120" preserveAspectRatio="none" aria-hidden="true">
        <defs>
          <linearGradient id="lwWaveA" x1="0" x2="1"><stop offset="0" stopColor="#3f6bff" stopOpacity="0" /><stop offset=".25" stopColor="#5b8cff" /><stop offset=".6" stopColor="#8b6cff" /><stop offset="1" stopColor="#8b6cff" stopOpacity="0" /></linearGradient>
          <linearGradient id="lwWaveB" x1="0" x2="1"><stop offset="0" stopColor="#5b8cff" stopOpacity="0" /><stop offset=".5" stopColor="#b48cff" stopOpacity=".7" /><stop offset="1" stopColor="#5b8cff" stopOpacity="0" /></linearGradient>
        </defs>
        <path d="M0 64 C 40 44, 75 30, 110 34 S 230 70, 420 76 S 800 84, 966 60" fill="none" stroke="url(#lwWaveA)" strokeWidth="1.6" />
        <path d="M0 70 C 50 52, 90 40, 140 46 S 260 82, 440 88 S 800 92, 966 74" fill="none" stroke="url(#lwWaveB)" strokeWidth="1" />
        {[[62, 22], [142, 38], [250, 60], [430, 78]].map(([x, y], i) => <circle key={i} cx={x} cy={y} r={i % 2 ? 1.2 : 1.8} fill="#9fb4ff" opacity=".8" />)}
      </svg>

      <div className="lw-head">
        <div className="lw-brand"><img src={agenticHrLogoTrans} alt="Lyra Workforce" /><div><b>Lyra <em className="lw-grad">Workforce</em></b><small>by AgentLyra</small></div></div>
        <div className="lw-nav" role="navigation" aria-label="Product areas">
          <span>RECRUIT</span><span>PLAN</span><span>ALLOCATE</span><span>GROW</span>
          <a className="lw-guide" href="/guide.html" target="_blank" rel="noopener noreferrer">Guide <ArrowRight size={14} style={{ transform: "rotate(-45deg)" }} /></a>
        </div>
      </div>

      <div className="lw-hero">
        <small className="lw-kicker">AI-POWERED WORKFORCE INTELLIGENCE</small>
        <h1>Know your workforce.<br />Plan your work.<br /><span>Hire only when you need to.</span></h1>
        <p>From people and projects to hiring and onboarding,<br />Lyra Workforce gives you a complete, AI-powered view<br />of your workforce — so you can make better decisions,<br />faster.</p>
        <div className="lw-cta">
          <button type="button" className="lw-btn-solid" onClick={onGetStarted}>Get started <ArrowRight size={18} strokeWidth={2.2} /></button>
          <a className="lw-btn-ghost" href="/guide.html" target="_blank" rel="noopener noreferrer"><CirclePlay size={22} strokeWidth={1.6} /> Watch a 2-min tour</a>
        </div>
      </div>

      <DashboardPreview />

      <ul className="lw-features">
        {FEATURES.map(([icon, tone, title, text], i) => (
          <li key={i}><span className={"lw-ic " + tone}>{icon}</span><b>{title}</b><p>{text}</p></li>
        ))}
      </ul>

      <div className="lw-stats">
        {STATS.map(([value, tone, label]) => (<div key={value}><b className={tone}>{value}</b><span>{label}</span></div>))}
      </div>

      <div className="lw-platform">
        <div className="lw-platform-copy"><b>Part of the AgentLyra platform</b><span>Lyra Workforce is one of the intelligent agents in the AgentLyra constellation.</span></div>
        <div className="lw-platform-icons">
          <div className="active"><span><img src={agenticHrLogo} alt="" className="lw-active-logo" /></span><small>Workforce</small></div>
          {PLATFORM.map(([icon, label]) => (<div key={label}><span>{icon}</span><small>{label}</small></div>))}
        </div>
      </div>
    </div>
  );
}

/* Right-hand panel. `children` replaces the sign-in card (e.g. the workspace picker). */
export function LoginRight({ subtitle, children }) {
  return (
    <div className="lw-right">
      <svg className="lw-orbits" viewBox="0 0 570 1024" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
        <ellipse cx="312" cy="470" rx="330" ry="330" fill="none" stroke="#ebe6e0" strokeWidth="1" strokeDasharray="2 5" />
        <ellipse cx="312" cy="470" rx="262" ry="262" fill="none" stroke="#efeae5" strokeWidth="1" strokeDasharray="2 5" />
        <circle cx="174" cy="75" r="6" fill="#f5b7a3" /><circle cx="519" cy="107" r="6" fill="#a9c1f5" />
        <circle cx="81" cy="919" r="6" fill="#b7c6f7" /><circle cx="282" cy="980" r="6" fill="#f6c4c4" />
      </svg>
      <div className="lw-right-inner">
        <small className="lw-welcome">WELCOME TO</small>
        <div className="lw-right-brand"><img src={agenticHrLogo} alt="Lyra Workforce" /><div><b>Lyra <em className="lw-grad">Workforce</em></b><span>by AgentLyra</span></div></div>
        <p className="lw-subtitle">{subtitle}</p>
        {children}
      </div>
    </div>
  );
}

export function LoginForm({
  inviteToken, creating, onToggleCreating, email, setEmail, password, setPassword, showPassword, setShowPassword,
  keepSignedIn, setKeepSignedIn, error, notice, busy, onSubmit, onGoogle, onForgot, passwordHint,
}) {
  return (
    <form className="lw-card" onSubmit={onSubmit}>
      {inviteToken && <p className="lw-invite">You’re invited — sign in with the email that received this invitation to join the workspace.</p>}
      {creating && !inviteToken && <p className="lw-invite">Create your account, then choose or create your workspace.</p>}

      <label className="lw-label" htmlFor="lw-email">Work email</label>
      <input id="lw-email" className="lw-input" required type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
      <label className="lw-label" htmlFor="lw-password">Password</label>
      <div className="lw-pass">
        <input id="lw-password" className="lw-input" required minLength={creating ? 8 : 6} type={showPassword ? "text" : "password"} autoComplete={creating ? "new-password" : "current-password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" />
        <button type="button" aria-label={showPassword ? "Hide password" : "Show password"} onClick={() => setShowPassword(!showPassword)}>{showPassword ? <EyeOff size={20} strokeWidth={1.6} /> : <Eye size={20} strokeWidth={1.6} />}</button>
      </div>
      {passwordHint}
      <div className="lw-row">
        <label className="lw-check"><input type="checkbox" checked={keepSignedIn} onChange={(e) => setKeepSignedIn(e.target.checked)} /><span aria-hidden="true"><svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.4 6.3 5 8.8 9.7 3.4" stroke="#fff" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" /></svg></span>Keep me signed in</label>
        <button type="button" className="lw-link" onClick={onForgot}>Forgot password?</button>
      </div>
      {error && <p className="lw-error" role="alert">{error}</p>}
      {notice && <p className="lw-notice" role="status">{notice}</p>}
      <button className="lw-submit" disabled={busy}>{busy ? "Please wait…" : <>{creating ? "Create account" : "Sign in"} <ArrowRight size={18} strokeWidth={2.2} /></>}</button>
      <div className="lw-or"><span>or</span></div>
      <button type="button" className="lw-google" onClick={onGoogle} disabled={busy}><GoogleG size={20} /> Continue with Google</button>
      <div className="lw-foot">{creating ? "Already have an account?" : "New to Lyra Workforce?"} <button type="button" onClick={onToggleCreating}>{creating ? "Sign in" : "Create an account"}</button></div>
    </form>
  );
}

/* The layout is designed at 1024px tall; scale it down on shorter windows so nothing is cut off. */
function useFitZoom() {
  const calc = () => Math.min(1, Math.max(0.5, window.innerHeight / 1024));
  const [zoom, setZoom] = useState(calc);
  useEffect(() => {
    const onResize = () => setZoom(calc());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return zoom;
}

export function LoginLayout({ subtitle, onGetStarted, children }) {
  const zoom = useFitZoom();
  return (
    <div className="lw-page" style={{ "--lw-zoom": zoom }}>
      <LoginHero onGetStarted={onGetStarted} />
      <LoginRight subtitle={subtitle}>{children}</LoginRight>
    </div>
  );
}
